<template>
	<div class="review-flow">
		<!-- Progress Indicator -->
		<div class="mb-3 text-center">
			<small class="text-muted">
				Reviewing creator {{ step }} of {{ total }}
			</small>
			<div class="progress mt-1" style="height: 4px;">
				<div class="progress-bar bg-success" :style="{ width: progressPercent + '%' }"></div>
			</div>
		</div>

		<!-- Action Buttons -->
		<div class="d-grid gap-2">
			<button @click.prevent="approve"
				class="btn btn-success btn-lg rounded-pill"
				:class="{ 'animate-approve': justApproved }"
				:disabled="isApproving">
				<i class="fe fe-check-circle me-1"></i>
				{{ justApproved ? 'Approved!' : 'Approve' }}
			</button>
			<button @click.prevent="skip"
				class="btn btn-outline-secondary rounded-pill"
				:disabled="isApproving">
				<i class="fe fe-skip-forward me-1"></i> Skip
			</button>
		</div>
	</div>
</template>

<script>
	export default {

		props: ['reviewData', 'approveEndpoint', 'model'],

		data() {
			const urlParams = new URLSearchParams(window.location.search);

			// Queue: ordered list of creator IDs still to review.
			// On first load, comes from server. On subsequent loads, passed via URL.
			const queueParam = urlParams.get('queue');
			let queue;
			if (queueParam) {
				queue = queueParam.split(',').map(Number).filter(id => !isNaN(id));
			} else {
				queue = [...this.reviewData.queue];
			}

			// Step: which creator number we're on (1-based). Advances on every action.
			const stepParam = urlParams.get('step');
			const step = stepParam ? parseInt(stepParam, 10) : 1;

			// Total: the original number of unapproved creators at the start of the review.
			// Stays fixed throughout the entire flow.
			const totalParam = urlParams.get('total');
			const total = totalParam ? parseInt(totalParam, 10) : this.reviewData.total;

			return {
				queue: queue,
				step: step,
				total: total,
				justApproved: false,
				isApproving: false,
			};
		},

		computed: {
			progressPercent() {
				if (this.total === 0) return 100;
				return Math.round(((this.step - 1) / this.total) * 100);
			},
		},

		methods: {
			approve() {
				this.isApproving = true;
				axios.get(this.approveEndpoint)
					.then((response) => {
						this.justApproved = true;

						// Remove current creator from queue (now approved)
						const updatedQueue = this.queue.filter(id => id !== this.reviewData.currentId);

						// Brief delay for visual feedback, then advance
						setTimeout(() => {
							this.advanceToNext(updatedQueue);
						}, 800);
					})
					.catch((error) => {
						console.error(error);
						this.isApproving = false;
					});
			},

			skip() {
				// Remove current from queue (skipped creators don't come back)
				const updatedQueue = this.queue.filter(id => id !== this.reviewData.currentId);
				this.advanceToNext(updatedQueue);
			},

			advanceToNext(updatedQueue) {
				const nextStep = this.step + 1;

				// If we've reviewed all creators, or queue is empty, go back to index
				if (nextStep > this.total || updatedQueue.length === 0) {
					window.location.href = this.reviewData.indexUrl;
					return;
				}

				const nextId = updatedQueue[0];
				const nextUrl = this.reviewData.urls[nextId];

				if (nextUrl) {
					window.location.href = nextUrl
						+ '?review=1'
						+ '&queue=' + updatedQueue.join(',')
						+ '&step=' + nextStep
						+ '&total=' + this.total;
				} else {
					window.location.href = this.reviewData.indexUrl;
				}
			}
		}

	}
</script>

<style scoped>
	.animate-approve {
		animation: pulse-approve 0.6s ease-in-out;
	}

	@keyframes pulse-approve {
		0% { transform: scale(1); }
		50% { transform: scale(1.15); }
		100% { transform: scale(1); }
	}
</style>
